import { Layout, util } from '@glu/core';
import GluIcons from '@glu/icons';
import { getClosestTheme } from '@glu/theming/glu-css-override';
import template from './alert.hbs';

import './alert.styles';

const gluIconsInstance = new GluIcons();
gluIconsInstance.registerIconHelper();

const defaultIconMap = {
  positive: 'tick',
  info: 'exclamation-solid',
  warning: 'exclamation-solid',
  negative: 'warning'
};
const mapTypeToIcon = (type, icons = defaultIconMap) => icons[type];

export default Layout.extend({
  template,

  attributes: {
    role: 'alert',
    tabindex: '-1'
  },

  ui: {
    $detailsBtn: '.btn-details',
    $detailsBtnIcon: '.btn-details span',
    $detailsBtnIconRight: '.caret-right-wrapper',
    $detailsBtnIconDown: '.caret-down-wrapper',
    $alertDetails: '.glu-alert-details-region'
  },

  regions: {
    alertDetailsRegion: '.glu-alert-details-region'
  },

  events: {
    'closed.bs.alert': 'close'
  },

  className() {
    const classes = ['alert', `alert-${this.options.type}`];
    if (this.options.canDismiss) {
      classes.push('alert-dismissible');
    }
    return classes.join(' ');
  },

  initialize(options) {
    util.defaults(this.options, options, {
      animate: false,
      message: '',
      title: '',
      type: 'default', // Type options are success, info, warning, danger
      duration: false,
      details: false,
      canDismiss: true,
      allowHtmlInTitle: false,
      allowHtmlInMessage: false
    });

    this.options.canDismiss = this.options.canDismiss === undefined || !!this.options.canDismiss;
  },

  templateHelpers() {
    return Object.assign({}, this.options, {
      alertIcon: this.options.icon,
      theme: (this.theme && this.theme.legacyAlert) || {},
      legacy: this.theme ? this.theme.legacy : true
    });
  },

  onRender() {
    this.$el.attr('class', this.className()); // options weren't ready the first time this ran
    if (this.options.details) {
      this.alertDetailsRegion.show(this.options.details);
    }
    if (this.options.duration) {
      setTimeout(this.close.bind(this), this.options.duration);
    }
  },

  onShow() {
    if (!this.shown) {
      this.shown = true;
      this.theme = getClosestTheme(this.$el);
      const { legacyAlert: { icons: themeIcons } = {} } = this.theme || {};
      this.options = Object.assign({
        icon: mapTypeToIcon(this.options.type, themeIcons)
      }, this.options);

      this.render();
    }

    // Note: when more than 1 instance, then autofocused will be the last down by page.
    if (this.options.autoFocus) {
      this.$el.focus();
    }
  },

  toggleDetails() {
    if (!this.shouldAnimate()) {
      this.ui.$alertDetails.toggle();
      this.handleDetailsButtons();
      return;
    }
    this.ui.$alertDetails.slideToggle(this.handleDetailsButtons.bind(this));
  },

  handleDetailsButtons() {
    if (this.ui.$alertDetails.is(':visible')) {
      this.ui.$detailsBtnIconDown.show();
      this.ui.$detailsBtnIconRight.hide();
      this.ui.$detailsBtnIcon.removeClass('icon-caret-down').addClass('icon-caret-up');
      return;
    }

    this.ui.$detailsBtnIconDown.hide();
    this.ui.$detailsBtnIconRight.show();

    this.ui.$detailsBtnIcon.removeClass('icon-caret-up').addClass('icon-caret-down');
  },

  close() {
    if (this.isClosed) {
      return;
    }
    if (!this.shouldAnimate()) { // GLU-1415 related
      Layout.prototype.close.call(this);
      return;
    }
    // fade out
    this.$el.fadeOut('slow', Layout.prototype.close.bind(this));
  },

  /**
   * Check for open modals. Mixing jQuery animation and CSS opacity causes display issues.
   * @return {boolean}
   * GLU-1415 related
   */
  shouldAnimate() {
    return this.options.animate && !window.document.body.classList.contains('modal-open');
  }
});
